/**
 * Tenant Handover Service
 * Handles the complete lifecycle of tenant changes in a unit
 * 
 * Features:
 * - Atomare Operation (alles oder nichts)
 * - Automatische Status-Verwaltung
 * - Audit Trail
 * - Fehlerbehandlung
 */

import { masterSupabase } from '../lib/supabase-master.js';

class TenantHandoverService {
  /**
   * Perform a complete tenant handover
   * @param {string} oldTenantId - ID of the outgoing tenant
   * @param {Object} newTenantData - Data for the new tenant
   * @param {Date} handoverDate - Date of the handover (default: today)
   * @returns {Promise<Object>} - The newly created tenant
   */
  async handoverTenant(oldTenantId, newTenantData, handoverDate = new Date()) {
    try {
      console.log('🔄 Starting tenant handover...', {
        oldTenantId,
        handoverDate: handoverDate.toISOString()
      });

      // Step 1: Get old tenant data
      const { data: oldTenant, error: fetchError } = await masterSupabase
        .from('tenants')
        .select('*')
        .eq('id', oldTenantId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch old tenant: ${fetchError.message}`);
      }

      if (!oldTenant) {
        throw new Error(`Tenant with ID ${oldTenantId} not found`);
      }

      console.log('✅ Old tenant found:', oldTenant.name);

      // Step 2: Validate old tenant is active
      if (oldTenant.status !== 'active') {
        throw new Error(`Old tenant is not active (status: ${oldTenant.status})`);
      }

      // Step 3: Terminate old tenant
      const handoverDateStr = handoverDate.toISOString().split('T')[0];
      
      const { error: updateError } = await masterSupabase
        .from('tenants')
        .update({
          status: 'inactive',
          lease_end: handoverDateStr,
          notes: oldTenant.notes 
            ? `${oldTenant.notes}\n\nAusgezogen am ${new Date().toLocaleDateString('de-DE')}`
            : `Ausgezogen am ${new Date().toLocaleDateString('de-DE')}`,
          updated_at: new Date().toISOString()
        })
        .eq('id', oldTenantId);

      if (updateError) {
        throw new Error(`Failed to terminate old tenant: ${updateError.message}`);
      }

      console.log('✅ Old tenant terminated');

      // Step 4: Wait for DB commit (ensure constraint is released)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Step 5: Create new tenant
      const newTenantPayload = {
        ...newTenantData,
        unit_id: oldTenant.unit_id,
        property_id: oldTenant.property_id,
        status: 'active',
        lease_start: handoverDateStr,
        created_at: new Date().toISOString()
      };

      const { data: newTenant, error: createError } = await masterSupabase
        .from('tenants')
        .insert([newTenantPayload])
        .select()
        .single();

      if (createError) {
        // Rollback: Reactivate old tenant
        console.error('❌ Failed to create new tenant, rolling back...');
        await masterSupabase
          .from('tenants')
          .update({
            status: 'active',
            lease_end: oldTenant.lease_end,
            updated_at: new Date().toISOString()
          })
          .eq('id', oldTenantId);

        throw new Error(`Failed to create new tenant: ${createError.message}`);
      }

      console.log('✅ New tenant created:', newTenant.name);

      // Step 6: Create audit log entry
      try {
        await masterSupabase
          .from('tenant_handover_log')
          .insert([{
            old_tenant_id: oldTenantId,
            new_tenant_id: newTenant.id,
            unit_id: oldTenant.unit_id,
            property_id: oldTenant.property_id,
            handover_date: handoverDateStr,
            old_tenant_name: oldTenant.name,
            new_tenant_name: newTenant.name,
            created_at: new Date().toISOString()
          }]);
      } catch (logError) {
        // Log error but don't fail the handover
        console.warn('⚠️ Failed to create audit log:', logError);
      }

      console.log('🎉 Tenant handover completed successfully!');

      return {
        success: true,
        oldTenant: {
          id: oldTenant.id,
          name: oldTenant.name,
          status: 'inactive'
        },
        newTenant: {
          id: newTenant.id,
          name: newTenant.name,
          status: 'active'
        },
        handoverDate: handoverDateStr
      };

    } catch (error) {
      console.error('💥 Tenant handover failed:', error);
      throw error;
    }
  }

  /**
   * Get handover history for a unit
   * @param {string} unitId - Unit ID
   * @returns {Promise<Array>} - List of handovers
   */
  async getHandoverHistory(unitId) {
    try {
      const { data, error } = await masterSupabase
        .from('tenant_handover_log')
        .select('*')
        .eq('unit_id', unitId)
        .order('handover_date', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Failed to fetch handover history:', error);
      return [];
    }
  }

  /**
   * Check if a unit has an active tenant
   * @param {string} unitId - Unit ID
   * @returns {Promise<Object|null>} - Active tenant or null
   */
  async getActiveTenant(unitId) {
    try {
      const { data, error } = await masterSupabase
        .from('tenants')
        .select('*')
        .eq('unit_id', unitId)
        .eq('status', 'active')
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Failed to check active tenant:', error);
      return null;
    }
  }

  /**
   * Validate if handover is possible
   * @param {string} oldTenantId - ID of the outgoing tenant
   * @returns {Promise<Object>} - Validation result
   */
  async validateHandover(oldTenantId) {
    try {
      // Get old tenant
      const { data: oldTenant, error } = await masterSupabase
        .from('tenants')
        .select('*')
        .eq('id', oldTenantId)
        .single();

      if (error || !oldTenant) {
        return {
          valid: false,
          reason: 'Tenant not found'
        };
      }

      // Check if active
      if (oldTenant.status !== 'active') {
        return {
          valid: false,
          reason: `Tenant is not active (status: ${oldTenant.status})`
        };
      }

      // Check if unit exists
      if (!oldTenant.unit_id) {
        return {
          valid: false,
          reason: 'Tenant has no unit assigned'
        };
      }

      return {
        valid: true,
        tenant: oldTenant
      };

    } catch (error) {
      return {
        valid: false,
        reason: error.message
      };
    }
  }
}

export default new TenantHandoverService();
